/**
 * Merkle Tree Service - OPFS-Mediated Protocol Implementation
 * 
 * Handles complete Merkle tree lifecycle for massive file transfers (30GB-100GB+):
 * - Binary tree storage and retrieval via OPFS
 * - Sender-side tree preparation and notification
 * - Receiver-side tree request and validation
 * - Protocol-aware chat/file channel separation
 * - Extension integration for native filesystem storage
 */

import { constructMerkleTree, generateMerkleProof, validateMerkleProof, hash } from '../lib/file';
import { merkleTreeWorkerService } from './merkle-tree-worker-service';
import { getFileFromOPFS, saveFileToOPFS, sanitizeOPFSFilename } from '../lib/file';
import { opfsIOWorkerService } from './opfs-io-worker-service';
import type { ExtensionBridge } from './extension-bridge';
import type { CheckpointService } from './checkpoint-service';

// Binary-efficient Merkle tree storage format
export interface MerkleTreeStorage {
  transferId: string;
  fileHash: string;
  fileName: string;
  chunkSize: number;
  totalChunks: number;
  rootHash: Uint8Array;
  
  // Binary tree data - level-by-level storage for efficiency
  levels: Uint8Array[];           // Flattened tree levels
  levelSizes: number[];           // Number of hashes in each level
  levelOffsets: number[];         // Byte offsets for level boundaries
  
  // Metadata
  timestamp: number;
  transferPath: 'opfs' | 'extension';
  treeSize: number;               // Total size in bytes
  
  // Performance metrics
  generationTime?: number;        // Time to generate tree (ms)
  memoryUsage?: number;           // Peak memory usage (bytes)
  
  // Transfer recovery metadata - enables resume after browser restart
  transferMetadata?: {
    fileName: string;
    fileSize: number;
    mimeType?: string;
    peerAddress: string;          // Remote peer address
    direction: 'inbound' | 'outbound';
    senderAddress?: string;       // For inbound transfers
    receiverAddress?: string;     // For outbound transfers
    
    // Phase 1: N/M progress tracking (receiver updates only)
    completedChunks?: number;     // N - last sequential chunk written (0-based)
    totalChunks?: number;          // M - total chunks in file
    lastChunkHash?: Uint8Array;   // Hash of last completed chunk (for resume validation)
    
    // GROUP TRANSFER: Preserve group flags for reconnect recovery (Bug 2 fix)
    // Without these, reconstructed records lose isGroupTransfer → stall detection
    // treats them as P2P and fires requestNextChunk, conflicting with group protocol.
    isGroupTransfer?: boolean;    // True if transfer uses group broadcast/seeder protocol
    groupAddress?: string;        // Group address for seeder announcements + pull window
    
    // EMAIL_P2P: Preserve email transfer flags for UI recovery after reconnect
    emailInitiated?: boolean;     // True if transfer was initiated via email attachment
    emailRecipient?: string;      // Email address of recipient (for email-initiated transfers)
    emailSubject?: string;        // Email subject for scoping attachment cards to specific emails
    
    createdAt: number;
    lastUpdated: number;
  };
  
  // Crypto receipt fields - for enterprise audit trails and NFT integration
  receiptData?: {
    contentCreator?: string;      // Creator address for copyright
    transferProof?: Uint8Array;   // Cryptographic signature
    blockchainRef?: string;       // Future: NFT/blockchain reference
    licenseInfo?: string;         // Content licensing metadata
  };
}

export interface MerkleTreeAvailableMessage {
  transferId: string;
  merkleTreeKey: string;
  treeSize: number;
  rootHash: Uint8Array;
  chunkSize: number;
  totalChunks: number;
  transferPath: 0 | 1;            // 0=OPFS, 1=extension
  timestamp: number;
}

export interface TreeTransferProgress {
  phase: 'generating' | 'storing' | 'notifying' | 'requesting' | 'downloading' | 'validating' | 'complete' | 'error';
  progress: number;               // 0-100
  bytesProcessed: number;
  totalBytes: number;
  message?: string;
  error?: string;
}

/**
 * Phase 1: Lightweight transfer state persistence
 * Stored as OPFS JSON file: transfer_state_{sessionId}.json
 * References merkle tree and file data without duplication
 */
export interface TransferPersistenceRecord {
  sessionId: string;              // Primary key - unique transfer session ID
  rootHash: string;               // File identifier (SHA-256 hash)
  peerAddress: string;            // Address of the remote peer (sender or receiver)
  direction: 'inbound' | 'outbound'; // Transfer direction from our perspective
  state: string;                  // FileTransferStateEnum value
  
  // File metadata for recovery
  fileName: string;
  fileSize: number;
  mimeType?: string;
  chunkSize: number;
  totalChunks: number;
  
  // References to OPFS files (not embedded data)
  merkleTreeKey: string;          // "merkle_tree_{sessionId}"
  opfsFilePath: string;           // "{rootHash}.tmp"
  transferPath?: 'opfs' | 'extension'; // Transfer mechanism
  
  // Timestamps
  createdAt: number;              // When transfer started
  lastUpdated: number;            // Last state update
}

export interface MerkleProof {
  chunkIndex: number;
  chunkHash: Uint8Array;
  siblings: Array<{ hash: Uint8Array; isLeft: boolean }>;
  rootHash: Uint8Array;
}

/**
 * Service for managing Merkle trees in massive file transfers
 */
export class MerkleTreeService {
  private static readonly TREE_PREFIX = 'merkle_tree_';
  private static readonly TREE_REQUEST_PREFIX = 'tree_request_';
  private static readonly MAX_TREE_AGE = 24 * 60 * 60 * 1000; // 24 hours
  private static readonly TREE_CHUNK_SIZE = 256 * 1024;       // 256KB chunks for tree transfer
  
  private extensionBridge?: ExtensionBridge;
  private checkpointService?: CheckpointService;
  private activeGenerations = new Map<string, Promise<MerkleTreeStorage>>();
  private treeCache = new Map<string, MerkleTreeStorage>();

  // P0-I Phase 1S-L2: bound the RAM-resident tree cache with an LRU.
  //
  // The treeCache is a hot-set (recently accessed trees in memory).  OPFS
  // is the cold queue: every tree we have ever cached remains persisted
  // there and is lazy-reloaded by the existing getStoredTree / loadTree /
  // generateProof code paths on RAM-cache miss.  Eviction here ONLY drops
  // the in-memory copy; the OPFS file is left untouched so that the
  // decentralized storage architecture can still serve the tree on demand
  // (e.g., when the file panel asks for hash metadata for a file the user
  // has not actively transferred recently).
  //
  // With ~25 trees in OPFS observed on K Linux cold start (each ~1000
  // chunk hashes), keeping all of them resident retained ~25k chunk
  // objects unnecessarily.  HOT_CACHE_MAX = 8 covers the worst-case
  // simultaneously-active transfer count (maxConcurrentTransfers = 3) plus
  // generous headroom for prefetch / proof-generation passes.
  private static readonly HOT_CACHE_MAX = 8;
  private treeCacheLRU: string[] = [];

  /**
   * Promote a transferId to most-recently-used and evict the least-recent
   * RAM entries if we exceed HOT_CACHE_MAX.  Eviction does NOT touch OPFS;
   * the tree remains lazy-reloadable by the OPFS-fallback paths.
   */
  private touchHotCache(transferId: string): void {
    const idx = this.treeCacheLRU.indexOf(transferId);
    if (idx >= 0) {
      this.treeCacheLRU.splice(idx, 1);
    }
    this.treeCacheLRU.push(transferId);
    while (this.treeCacheLRU.length > MerkleTreeService.HOT_CACHE_MAX) {
      const evictedId = this.treeCacheLRU.shift();
      if (evictedId !== undefined) {
        // RAM-only eviction; OPFS persistence intact.
        this.treeCache.delete(evictedId);
      }
    }
  }

  /**
   * RAM-cache getter that promotes the entry on hit.  Use this in place of
   * `this.treeCache.get(...)` everywhere inside the service.  Returns
   * undefined on miss; callers fall through to OPFS load.
   */
  private cacheGetTree(transferId: string): MerkleTreeStorage | undefined {
    const cache = this.treeCache;
    const tree = cache.get(transferId);
    if (tree !== undefined) {
      this.touchHotCache(transferId);
    }
    return tree;
  }

  /**
   * RAM-cache setter that promotes the entry to MRU and evicts old
   * entries when over HOT_CACHE_MAX.  Use this in place of
   * `this.treeCache.set(...)` everywhere inside the service.
   */
  private cacheSetTree(
    transferId: string,
    treeStorage: MerkleTreeStorage,
  ): void {
    const cache = this.treeCache;
    cache.set(transferId, treeStorage);
    this.touchHotCache(transferId);
  }
  
  /**
   * Private peer context for receiver-side metadata population
   */
  private peerAddress: string | null = null;
  private nodePublicKey: string | null = null;
  
  /**
   * WRITE-BEFORE-READ GUARD: Track pending OPFS write operations
   * Prevents Invalid DataView errors when read occurs during write
   * Maps treeKey → Promise that resolves when write completes
   */
  private pendingWrites = new Map<string, Promise<void>>();

  constructor(extensionBridge?: ExtensionBridge, checkpointService?: CheckpointService) {
    this.extensionBridge = extensionBridge;
    this.checkpointService = checkpointService;
  }
  
  /**
   * Phase 2: Set peer context for receiver-side metadata population
   * Should be called before requestAndValidateMerkleTree
   */
  setPeerContext(peerAddress: string, userAddress: string): void {
    this.peerAddress = peerAddress;
    this.nodePublicKey = userAddress;
  }

  /**
   * PERFORMANCE FIX: Store pre-generated Merkle tree without regenerating
   * This method reuses an already-computed tree, avoiding 60+ second regeneration
   * for large files (6GB+)
   */
  async prepareMerkleTreeFromExisting(
    existingTree: Uint8Array[][],
    transferId: string,
    chunkSize: number,
    transferPath: 'opfs' | 'extension' = 'opfs',
    transferMetadata?: {
      fileName: string;
      fileSize: number;
      mimeType?: string;
      peerAddress: string;
      direction: 'inbound' | 'outbound';
      senderAddress?: string;
      receiverAddress?: string;
    },
    onProgress?: (progress: TreeTransferProgress) => void
  ): Promise<MerkleTreeAvailableMessage> {
    
    const startTime = Date.now();
    const treeKey = this.generateTreeKey(transferId);
    
    console.log('[MerkleTreeService] Using pre-generated Merkle tree', {
      transferId: transferId.slice(0, 8) + '...',
      levels: existingTree.length,
      transferPath,
      performance: 'OPTIMIZED - skip regeneration'
    });
    
    // Check if already in progress
    const existing = this.activeGenerations.get(transferId);
    if (existing) {
      console.log('[MerkleTreeService] Tree already stored, reusing...');
      const treeStorage = await existing;
      return this.createAvailableMessage(treeStorage);
    }
    
    // Convert existing tree to storage format
    const generationPromise = this.convertTreeToStorage(
      existingTree, transferId, chunkSize, transferPath, transferMetadata
    );
    
    this.activeGenerations.set(transferId, generationPromise);
    
    try {
      const treeStorage = await generationPromise;
      
      console.log('[MerkleTreeService] Tree converted, now storing...', {
        transferId: transferId.slice(0, 8) + '...',
        treeSize: treeStorage.treeSize,
        levels: treeStorage.levelSizes.length
      });
      
      // Store tree using appropriate path
      await this.storeTreeData(treeStorage, transferPath, onProgress);
      
      console.log('[MerkleTreeService] Tree stored successfully');
      
      // Cache for quick access
      this.cacheSetTree(transferId, treeStorage);
      
      console.log('[MerkleTreeService] Pre-generated tree stored', {
        transferId: transferId.slice(0, 8) + '...',
        rootHash: Array.from(treeStorage.rootHash.slice(0, 8))
          .map(b => b.toString(16).padStart(2, '0'))
          .join(''),
        levels: treeStorage.levelSizes.length,
        totalChunks: treeStorage.totalChunks,
        processingTime: `${Date.now() - startTime}ms`,
        storagePath: transferPath
      });
      
      onProgress?.({
        phase: 'complete',
        progress: 100,
        bytesProcessed: treeStorage.treeSize,
        totalBytes: treeStorage.treeSize,
        message: `Tree ready for ${treeStorage.totalChunks} chunks`
      });
      
      return this.createAvailableMessage(treeStorage);
      
    } finally {
      this.activeGenerations.delete(transferId);
    }
  }
  
  /**
   * Generate and store Merkle tree for sender-side preparation
   * This is called during file preparation phase
   */
  async prepareMerkleTreeForTransfer(
    file: File,
    transferId: string,
    chunkSize: number,
    transferPath: 'opfs' | 'extension' = 'opfs',
    transferMetadata?: {
      fileName: string;
      fileSize: number;
      mimeType?: string;
      peerAddress: string;
      direction: 'inbound' | 'outbound';
      senderAddress?: string;
      receiverAddress?: string;
    },
    onProgress?: (progress: TreeTransferProgress) => void
  ): Promise<MerkleTreeAvailableMessage> {
    
    const startTime = Date.now();
    const fileHash = await this.calculateFileHash(file);
    const treeKey = this.generateTreeKey(transferId);

    console.log('[MerkleTreeService] Preparing tree for transfer', {
      transferId: transferId.slice(0, 8) + '...',
      fileName: file.name,
      fileSize: file.size,
      chunkSize,
      transferPath
    });

    // Check if we're already generating this tree
    const existing = this.activeGenerations.get(transferId);
    if (existing) {
      console.log('[MerkleTreeService] Tree generation already in progress, waiting...');
      const treeStorage = await existing;
      return this.createAvailableMessage(treeStorage);
    }

    // Start tree generation
    const generationPromise = this.generateTreeStorage(
      file, transferId, fileHash, chunkSize, transferPath, transferMetadata, onProgress
    );
    
    this.activeGenerations.set(transferId, generationPromise);

    try {
      const treeStorage = await generationPromise;
      
      console.log('[MerkleTreeService] Tree generated, now storing...', {
        transferId: transferId.slice(0, 8) + '...',
        treeSize: treeStorage.treeSize,
        levels: treeStorage.levelSizes.length
      });
      
      // Store tree using appropriate path
      await this.storeTreeData(treeStorage, transferPath, onProgress);
      
      console.log('[MerkleTreeService] Tree stored successfully in OPFS/extension');
      
      // Cache for quick access
      this.cacheSetTree(transferId, treeStorage);
      
      console.log('[MerkleTreeService] Tree prepared successfully', {
        transferId: transferId.slice(0, 8) + '...',
        rootHash: Array.from(treeStorage.rootHash.slice(0, 8))
          .map(b => b.toString(16).padStart(2, '0'))
          .join(''),
        levels: treeStorage.levelSizes.length,
        totalChunks: treeStorage.totalChunks,
        generationTime: `${treeStorage.generationTime}ms`,
        storagePath: transferPath
      });

      onProgress?.({
        phase: 'complete',
        progress: 100,
        bytesProcessed: treeStorage.treeSize,
        totalBytes: treeStorage.treeSize,
        message: `Tree ready for ${treeStorage.totalChunks} chunks`
      });

      return this.createAvailableMessage(treeStorage);

    } finally {
      this.activeGenerations.delete(transferId);
    }
  }

  /**
   * Handle receiver-side tree request and validation
   * This is called when receiver gets MERKLE_TREE_AVAILABLE message
   */
  async requestAndValidateMerkleTree(
    availableMessage: MerkleTreeAvailableMessage,
    fileChannel: any, // WebRTC file channel
    onProgress?: (progress: TreeTransferProgress) => void
  ): Promise<MerkleTreeStorage> {

    console.log('[MerkleTreeService] Requesting Merkle tree from sender', {
      transferId: availableMessage.transferId.slice(0, 8) + '...',
      treeSize: availableMessage.treeSize,
      totalChunks: availableMessage.totalChunks
    });

    onProgress?.({
      phase: 'requesting',
      progress: 10,
      bytesProcessed: 0,
      totalBytes: availableMessage.treeSize,
      message: 'Requesting tree from sender'
    });

    // Request tree via file channel using special message type
    const treeRequestMessage = {
      type: 'MERKLE_TREE_REQUEST',
      transferId: availableMessage.transferId,
      merkleTreeKey: availableMessage.merkleTreeKey
    };

    // Send tree request (must be binary for file channel)
    const requestData = new TextEncoder().encode(JSON.stringify(treeRequestMessage));
    fileChannel.send(requestData);

    // Download tree data via file channel (reuses chunk protocol)
    const treeData = await this.downloadTreeViaFileChannel(
      fileChannel, 
      availableMessage,
      onProgress
    );

    onProgress?.({
      phase: 'validating',
      progress: 90,
      bytesProcessed: treeData.length,
      totalBytes: availableMessage.treeSize,
      message: 'Validating tree integrity'
    });

    // Parse and validate tree
    const treeStorage = await this.parseAndValidateTreeData(treeData, availableMessage);

    // Phase 2: Enhance with receiver metadata if not already populated by sender
    if (!treeStorage.transferMetadata && this.peerAddress && this.nodePublicKey) {
      treeStorage.transferMetadata = {
        fileName: treeStorage.fileName || 'unknown',
        fileSize: treeStorage.totalChunks * treeStorage.chunkSize, // Approximate
        mimeType: undefined,
        peerAddress: this.peerAddress,  // Sender address
        direction: 'inbound',
        senderAddress: this.peerAddress,
        receiverAddress: this.nodePublicKey,  // Our address
        createdAt: Date.now(),
        lastUpdated: Date.now()
      };
      
      console.log('[MerkleTreeService] Enriched tree with receiver metadata', {
        transferId: availableMessage.transferId.slice(0, 8) + '...',
        peerAddress: this.peerAddress.slice(0, 8) + '...',
        direction: 'inbound'
      });
    } else if (treeStorage.transferMetadata) {
      // Update direction for receiver (sender might have sent it as outbound)
      treeStorage.transferMetadata.direction = 'inbound';
      treeStorage.transferMetadata.receiverAddress = this.nodePublicKey ?? undefined;
      treeStorage.transferMetadata.lastUpdated = Date.now();
      
      console.log('[MerkleTreeService] Updated tree metadata for receiver', {
        transferId: availableMessage.transferId.slice(0, 8) + '...',
        direction: 'inbound'
      });
    }

    // Store locally for chunked access
    const transferPath = availableMessage.transferPath === 1 ? 'extension' : 'opfs';
    await this.storeTreeData(treeStorage, transferPath);

    // Cache for validation during transfer
    this.cacheSetTree(availableMessage.transferId, treeStorage);

    console.log('[MerkleTreeService] Tree download and validation complete', {
      transferId: availableMessage.transferId.slice(0, 8) + '...',
      treeSize: treeStorage.treeSize,
      rootHashMatch: this.uint8ArrayEquals(treeStorage.rootHash, availableMessage.rootHash)
    });

    onProgress?.({
      phase: 'complete',
      progress: 100,
      bytesProcessed: treeStorage.treeSize,
      totalBytes: treeStorage.treeSize,
      message: 'Tree ready for chunk validation'
    });

    return treeStorage;
  }

  /**
   * Validate a specific chunk using the Merkle tree
   * This is called during chunk reception/assembly
   */
  async validateChunkWithTree(
    transferId: string,
    chunkIndex: number,
    chunkData: Uint8Array,
    expectedHash?: Uint8Array
  ): Promise<{ valid: boolean; proof?: Uint8Array[]; computedHash?: Uint8Array }> {
    
    const treeStorage = this.cacheGetTree(transferId);
    if (!treeStorage) {
      console.warn('[MerkleTreeService] No tree available for chunk validation', { transferId });
      return { valid: false };
    }

    // Compute chunk hash
    const computedHash = await hash(chunkData as BufferSource);

    // Generate proof from stored tree
    const tree = this.reconstructTreeFromStorage(treeStorage);
    const proofResult = await this.generateMerkleProofFromTree(tree, chunkIndex);
    const { proof } = proofResult;

    // Validate proof
    const rootHash = await validateMerkleProof(proof, computedHash, chunkIndex);
    const valid = this.uint8ArrayEquals(rootHash, treeStorage.rootHash);

    if (!valid) {
      console.error('[MerkleTreeService] Chunk validation failed', {
        transferId: transferId.slice(0, 8) + '...',
        chunkIndex,
        expectedRoot: this.uint8ArrayToHex(treeStorage.rootHash).slice(0, 16) + '...',
        computedRoot: this.uint8ArrayToHex(rootHash).slice(0, 16) + '...'
      });
    }

    return { valid, proof: proof, computedHash };
  }

  /**
   * Generate a complete Merkle proof for a specific chunk
   * Returns the sibling hashes needed to reconstruct the root
   */
  async generateMerkleProofFromTree(
    tree: Uint8Array[][],
    chunkIndex: number
  ): Promise<{ proof: Uint8Array[]; siblings: Array<{ hash: Uint8Array; isLeft: boolean }> }> {
    
    const proof: Uint8Array[] = [];
    const siblings: Array<{ hash: Uint8Array; isLeft: boolean }> = [];
    
    let currentIndex = chunkIndex;
    
    // Traverse from leaf to root
    for (let level = 0; level < tree.length - 1; level++) {
      const levelHashes = tree[level];
      
      // Determine sibling index
      const siblingIndex = currentIndex % 2 === 0 ? currentIndex + 1 : currentIndex - 1;
      
      // Add sibling to proof if it exists
      if (siblingIndex < levelHashes.length) {
        const siblingHash = levelHashes[siblingIndex];
        proof.push(siblingHash);
        siblings.push({
          hash: siblingHash,
          isLeft: siblingIndex < currentIndex
        });
      }
      
      // Move to parent index for next level
      currentIndex = Math.floor(currentIndex / 2);
    }
    
    return { proof, siblings };
  }

  /**
   * Generate a proof for a specific chunk (public API)
   * This can be used by offscreen document for on-demand proof generation
   */
  async generateProofForChunk(
    transferId: string,
    chunkIndex: number,
    chunkData?: Uint8Array
  ): Promise<MerkleProof | null> {
    
    let treeStorage = this.cacheGetTree(transferId);
    if (!treeStorage) {
      // Try loading from storage
      const loaded = await this.loadTreeData(transferId, 'opfs');
      if (!loaded) {
        console.warn('[MerkleTreeService] No tree available for proof generation', { transferId });
        return null;
      }
      this.cacheSetTree(transferId, loaded);
      treeStorage = loaded;
    }
    
    const tree = this.reconstructTreeFromStorage(treeStorage);
    const { proof, siblings } = await this.generateMerkleProofFromTree(tree, chunkIndex);
    
    // Get chunk hash (from tree or compute from data)
    let chunkHash: Uint8Array;
    if (chunkData) {
      chunkHash = await hash(chunkData as BufferSource);
    } else {
      // Get from stored tree
      chunkHash = tree[0][chunkIndex]; // Leaf level
    }
    
    return {
      chunkIndex,
      chunkHash,
      siblings,
      rootHash: treeStorage?.rootHash || tree[tree.length - 1][0]
    };
  }

  /**
   * Get tree statistics for monitoring
   */
  getTreeStats(transferId: string): {
    exists: boolean;
    treeSize?: number;
    totalChunks?: number;
    generationTime?: number;
    memoryUsage?: number;
  } {
    const treeStorage = this.cacheGetTree(transferId);
    if (!treeStorage) {
      return { exists: false };
    }

    return {
      exists: true,
      treeSize: treeStorage.treeSize,
      totalChunks: treeStorage.totalChunks,
      generationTime: treeStorage.generationTime,
      memoryUsage: treeStorage.memoryUsage
    };
  }

  /**
   * Phase 4: Get full tree storage for recovery
   * Returns complete MerkleTreeStorage including metadata
   */
  async getStoredTreeForRecovery(merkleTreeKey: string): Promise<MerkleTreeStorage | null> {
    try {
      // Extract transferId from the key (remove prefix if present)
      const transferId = merkleTreeKey.startsWith(MerkleTreeService.TREE_PREFIX) 
        ? merkleTreeKey.replace(MerkleTreeService.TREE_PREFIX, '')
        : merkleTreeKey;
      
      // First check cache
      const cached = this.cacheGetTree(transferId);
      if (cached) {
        return cached;
      }
      
      // Try loading from storage
      const treeStorage = await this.loadTreeData(transferId, 'opfs');
      if (treeStorage) {
        this.cacheSetTree(transferId, treeStorage);
        return treeStorage;
      }
      
      // Try with the full key if transferId didn't work
      const treeStorageWithKey = await this.loadTreeDataByKey(merkleTreeKey, 'opfs');
      if (treeStorageWithKey) {
        this.cacheSetTree(transferId, treeStorageWithKey);
        return treeStorageWithKey;
      }
      
      return null;
    } catch (error) {
      console.error('[MerkleTreeService] Error getting tree for recovery:', error);
      return null;
    }
  }

  /**
   * Get stored tree data for a given key
   * Public wrapper for loadTreeData for NFS chunk writing
   */
  async getStoredTree(merkleTreeKey: string): Promise<{
    root?: Uint8Array;
    nodes?: Uint8Array[];
  } | null> {
    try {
      // Extract transferId from the key (remove prefix if present)
      const transferId = merkleTreeKey.startsWith(MerkleTreeService.TREE_PREFIX) 
        ? merkleTreeKey.replace(MerkleTreeService.TREE_PREFIX, '')
        : merkleTreeKey;
      
      console.log('[MerkleTreeService] Looking for stored tree', {
        merkleTreeKey,
        transferId,
        hasCached: this.treeCache.has(transferId)
      });
      
      // First check cache
      const cached = this.cacheGetTree(transferId);
      if (cached) {
        const tree = this.reconstructTreeFromStorage(cached);
        return {
          root: cached.rootHash,
          nodes: tree[0] // Leaf level contains chunk hashes
        };
      }
      
      // Try loading from storage - use the full key as it was saved
      const treeStorage = await this.loadTreeData(transferId, 'opfs');
      if (!treeStorage) {
        // Try with the full key if transferId didn't work
        console.log('[MerkleTreeService] Trying with full key', { merkleTreeKey });
        const treeStorageWithKey = await this.loadTreeDataByKey(merkleTreeKey, 'opfs');
        if (!treeStorageWithKey) {
          return null;
        }
        // Cache it for future use
        this.cacheSetTree(transferId, treeStorageWithKey);
        const tree = this.reconstructTreeFromStorage(treeStorageWithKey);
        return {
          root: treeStorageWithKey.rootHash,
          nodes: tree[0]
        };
      }
      
      // Cache it for future use
      this.cacheSetTree(transferId, treeStorage);
      
      const tree = this.reconstructTreeFromStorage(treeStorage);
      return {
        root: treeStorage.rootHash,
        nodes: tree[0] // Leaf level contains chunk hashes
      };
    } catch (error) {
      console.warn('[MerkleTreeService] Failed to get stored tree:', error);
      return null;
    }
  }

  /**
   * Cleanup expired trees
   */
  async cleanupExpiredTrees(): Promise<void> {
    const now = Date.now();
    const expiredKeys: string[] = [];

    for (const [transferId, treeStorage] of this.treeCache.entries()) {
      if (now - treeStorage.timestamp > MerkleTreeService.MAX_TREE_AGE) {
        expiredKeys.push(transferId);
        
        // Remove from OPFS/extension
        try {
          const treeKey = this.generateTreeKey(transferId);
          if (treeStorage.transferPath === 'extension' && this.extensionBridge) {
            // Extension cleanup would go here
            console.log('[MerkleTreeService] Extension tree cleanup not implemented yet');
          } else {
            // OPFS cleanup
            const file = await getFileFromOPFS(treeKey);
            if (file) {
              console.log('[MerkleTreeService] Cleaning up expired tree', { transferId });
              // OPFS deletion would require additional API
            }
          }
        } catch (error) {
          console.warn('[MerkleTreeService] Failed to cleanup tree', { transferId, error });
        }
      }
    }

    // Remove from cache (and from LRU bookkeeping)
    expiredKeys.forEach(key => {
      this.treeCache.delete(key);
      const lruIdx = this.treeCacheLRU.indexOf(key);
      if (lruIdx >= 0) {
        this.treeCacheLRU.splice(lruIdx, 1);
      }
    });
    
    if (expiredKeys.length > 0) {
      console.log('[MerkleTreeService] Cleaned up expired trees', { count: expiredKeys.length });
    }
  }

  // Private helper methods
  
  /**
   * Convert pre-generated Merkle tree to storage format
   * Used by prepareMerkleTreeFromExisting to avoid regeneration
   */
  private async convertTreeToStorage(
    tree: Uint8Array[][],
    transferId: string,
    chunkSize: number,
    transferPath: 'opfs' | 'extension',
    transferMetadata?: {
      fileName: string;
      fileSize: number;
      mimeType?: string;
      peerAddress: string;
      direction: 'inbound' | 'outbound';
      senderAddress?: string;
      receiverAddress?: string;
    }
  ): Promise<MerkleTreeStorage> {
    
    const startTime = Date.now();
    const rootHash = tree[tree.length - 1][0]; // Root is top level, first hash
    const totalChunks = tree[0].length; // Number of leaf hashes
    
    // Flatten tree into binary storage format
    const { levels, levelSizes, levelOffsets, treeSize } = this.flattenTree(tree);
    
    const treeStorage: MerkleTreeStorage = {
      transferId,
      fileHash: '', // Not needed for pre-generated trees
      fileName: transferMetadata?.fileName || '', 
      chunkSize,
      totalChunks,
      rootHash,
      levels,
      levelSizes,
      levelOffsets,
      timestamp: Date.now(),
      transferPath,
      treeSize,
      generationTime: Date.now() - startTime,
      memoryUsage: this.estimateMemoryUsage(tree),
      // Phase 2: Populate transfer recovery metadata
      // FIX 7B: Include totalChunks and completedChunks for recovery progress tracking
      transferMetadata: transferMetadata ? {
        fileName: transferMetadata.fileName,
        fileSize: transferMetadata.fileSize,
        mimeType: transferMetadata.mimeType,
        peerAddress: transferMetadata.peerAddress,
        direction: transferMetadata.direction,
        senderAddress: transferMetadata.senderAddress,
        receiverAddress: transferMetadata.receiverAddress,
        totalChunks,
        completedChunks: 0,
        createdAt: Date.now(),
        lastUpdated: Date.now()
      } : undefined
    };
    
    return treeStorage;
  }

  private async generateTreeStorage(
    file: File,
    transferId: string,
    fileHash: string,
    chunkSize: number,
    transferPath: 'opfs' | 'extension',
    transferMetadata?: {
      fileName: string;
      fileSize: number;
      mimeType?: string;
      peerAddress: string;
      direction: 'inbound' | 'outbound';
      senderAddress?: string;
      receiverAddress?: string;
    },
    onProgress?: (progress: TreeTransferProgress) => void
  ): Promise<MerkleTreeStorage> {
    
    const startTime = Date.now();
    const totalChunks = Math.ceil(file.size / chunkSize);

    onProgress?.({
      phase: 'generating',
      progress: 20,
      bytesProcessed: 0,
      totalBytes: file.size,
      message: `Generating tree for ${totalChunks} chunks`
    });

    // Generate Merkle tree (worker-offloaded with main-thread fallback)
    const tree = await merkleTreeWorkerService.buildTree(file, chunkSize);
    const rootHash = tree[tree.length - 1][0]; // Root is top level, first hash

    onProgress?.({
      phase: 'storing',
      progress: 60,
      bytesProcessed: file.size,
      totalBytes: file.size,
      message: 'Flattening tree structure'
    });

    // Flatten tree into binary storage format
    const { levels, levelSizes, levelOffsets, treeSize } = this.flattenTree(tree);

    const treeStorage: MerkleTreeStorage = {
      transferId,
      fileHash,
      fileName: file.name,
      chunkSize,
      totalChunks,
      rootHash,
      levels,
      levelSizes,
      levelOffsets,
      timestamp: Date.now(),
      transferPath,
      treeSize,
      generationTime: Date.now() - startTime,
      memoryUsage: this.estimateMemoryUsage(tree),
      // Phase 2: Populate transfer recovery metadata
      // FIX 7B: Include totalChunks and completedChunks for recovery progress tracking
      transferMetadata: transferMetadata ? {
        fileName: transferMetadata.fileName,
        fileSize: transferMetadata.fileSize,
        mimeType: transferMetadata.mimeType,
        peerAddress: transferMetadata.peerAddress,
        direction: transferMetadata.direction,
        senderAddress: transferMetadata.senderAddress,
        receiverAddress: transferMetadata.receiverAddress,
        totalChunks,
        completedChunks: 0,
        createdAt: Date.now(),
        lastUpdated: Date.now()
      } : undefined
    };

    return treeStorage;
  }

  private flattenTree(tree: Uint8Array[][]): {
    levels: Uint8Array[];
    levelSizes: number[];
    levelOffsets: number[];
    treeSize: number;
  } {
    const levels: Uint8Array[] = [];
    const levelSizes: number[] = [];
    const levelOffsets: number[] = [];
    let currentOffset = 0;

    for (const level of tree) {
      const levelData = new Uint8Array(level.length * 32); // 32 bytes per SHA-256 hash
      let pos = 0;
      
      for (const hash of level) {
        levelData.set(hash, pos);
        pos += 32;
      }
      
      levels.push(levelData);
      levelSizes.push(level.length);
      levelOffsets.push(currentOffset);
      currentOffset += levelData.length;
    }

    const treeSize = levels.reduce((sum, level) => sum + level.length, 0);
    return { levels, levelSizes, levelOffsets, treeSize };
  }

  /**
   * Phase 2: Load tree data by key
   * Public to allow sender to retrieve tree for chunk 0 padding
   */
  public async loadTreeDataByKey(
    treeKey: string,
    transferPath: 'opfs' | 'extension' = 'opfs',
    onProgress?: (progress: TreeTransferProgress) => void
  ): Promise<MerkleTreeStorage | null> {
    // WRITE-BEFORE-READ GUARD: Wait for any pending write to complete
    // This prevents Invalid DataView errors from reading partial data
    const pendingWrite = this.pendingWrites.get(treeKey);
    if (pendingWrite) {
      console.log('[MerkleTreeService] Waiting for pending write to complete before read', { treeKey });
      try {
        await pendingWrite;
      } catch {
        // Write failed, but we still try to read (may have old data)
      }
    }
    
    // Load directly using the provided key
    try {
      if (transferPath === 'opfs') {
        // Route through OPFS I/O worker (sync access handle) when available,
        // falls back to main-thread async OPFS automatically
        const treeData = await opfsIOWorkerService.read(treeKey);
        if (!treeData) {
          console.warn('[MerkleTreeService] Tree not found in OPFS with key', { treeKey });
          return null;
        }
        
        const treeStorage = this.deserializeTreeStorage(treeData);
        
        // CRITICAL FIX: Use actual file size as treeSize, not the JSON-stored value
        // The JSON stores the raw treeSize before serialization overhead was added.
        // This fixes FILE_METADATA sending wrong merkleTreeSize on stall recovery.
        const actualTreeSize = treeData.byteLength;
        if (treeStorage.treeSize !== actualTreeSize) {
          console.log('[MerkleTreeService] Correcting treeSize from stored value to actual', {
            storedSize: treeStorage.treeSize,
            actualSize: actualTreeSize,
            transferId: treeStorage.transferId?.slice(0, 8) + '...'
          });
          treeStorage.treeSize = actualTreeSize;
        }
        
        onProgress?.({
          phase: 'complete',
          progress: 100,
          bytesProcessed: treeStorage.treeSize,
          totalBytes: treeStorage.treeSize,
          message: 'Tree loaded from OPFS'
        });
        
        return treeStorage;
      }
    } catch (error) {
      console.error('[MerkleTreeService] Failed to load tree by key:', error);
    }
    return null;
  }

  private async loadTreeData(
    transferId: string,
    transferPath: 'opfs' | 'extension',
    onProgress?: (progress: TreeTransferProgress) => void
  ): Promise<MerkleTreeStorage | null> {
    
    const treeKey = this.generateTreeKey(transferId);
    
    onProgress?.({
      phase: 'downloading',
      progress: 0,
      bytesProcessed: 0,
      totalBytes: 0,
      message: `Loading Merkle tree from ${transferPath}`
    });
    
    try {
      if (transferPath === 'extension' && this.extensionBridge) {
        // Load from extension storage
        console.log('[MerkleTreeService] Loading tree from extension', { treeKey });
        
        const result = await this.extensionBridge.invokeExtensionWorker(
          'RESTORE_CHECKPOINT',
          {
            fileName: `${treeKey}.merkle`,
            transferId: transferId,
            fileType: 'merkle-tree'
          },
          15000 // 15 second timeout for tree loading
        );
        
        if (!result.success || !result.data) {
          console.warn('[MerkleTreeService] Extension tree load failed, trying OPFS fallback');
          return await this.loadTreeData(transferId, 'opfs', onProgress);
        }
        
        const treeStorage = this.deserializeTreeStorage(new Uint8Array(result.data));
        
        // CRITICAL FIX: Use actual data size as treeSize, not the JSON-stored value
        // The JSON stores the raw treeSize before serialization overhead was added.
        const actualTreeSize = result.data.length;
        if (treeStorage.treeSize !== actualTreeSize) {
          console.log('[MerkleTreeService] Correcting treeSize from stored value to actual (extension)', {
            storedSize: treeStorage.treeSize,
            actualSize: actualTreeSize,
            transferId: treeStorage.transferId?.slice(0, 8) + '...'
          });
          treeStorage.treeSize = actualTreeSize;
        }
        
        onProgress?.({
          phase: 'complete',
          progress: 100,
          bytesProcessed: treeStorage.treeSize,
          totalBytes: treeStorage.treeSize,
          message: 'Tree loaded from extension'
        });
        
        return treeStorage;
        
      } else {
        // Load from OPFS — routed through I/O worker when available
        const treeData = await opfsIOWorkerService.read(treeKey);
        if (!treeData) {
          console.warn('[MerkleTreeService] Tree not found in OPFS', { treeKey });
          return null;
        }
        
        const treeStorage = this.deserializeTreeStorage(treeData);
        
        // CRITICAL FIX: Use actual file size as treeSize, not the JSON-stored value
        // The JSON stores the raw treeSize before serialization overhead was added.
        // This fixes FILE_METADATA sending wrong merkleTreeSize on stall recovery.
        const actualTreeSize = treeData.byteLength;
        if (treeStorage.treeSize !== actualTreeSize) {
          console.log('[MerkleTreeService] Correcting treeSize from stored value to actual', {
            storedSize: treeStorage.treeSize,
            actualSize: actualTreeSize,
            transferId: treeStorage.transferId?.slice(0, 8) + '...'
          });
          treeStorage.treeSize = actualTreeSize;
        }
        
        onProgress?.({
          phase: 'complete',
          progress: 100,
          bytesProcessed: treeStorage.treeSize,
          totalBytes: treeStorage.treeSize,
          message: 'Tree loaded from OPFS'
        });
        
        return treeStorage;
      }
      
    } catch (error) {
      console.error('[MerkleTreeService] Failed to load tree', { transferId, error });
      return null;
    }
  }

  /**
   * Phase 3: Store tree data to OPFS/extension
   * Public to allow receiver to store extracted tree from chunk 0
   */
  public async storeTreeData(
    treeStorage: MerkleTreeStorage,
    transferPath: 'opfs' | 'extension',
    onProgress?: (progress: TreeTransferProgress) => void
  ): Promise<void> {
    
    const treeKey = this.generateTreeKey(treeStorage.transferId);
    const storageData = this.serializeTreeStorage(treeStorage);
    
    // CRITICAL FIX: Update treeSize to match ACTUAL serialized size
    // This fixes the receiver's "tree size mismatch" error in Phase 3 extraction
    // The serialized size includes metadata overhead (4 byte header + JSON + root hash + tree levels)
    const rawTreeSize = treeStorage.treeSize;
    treeStorage.treeSize = storageData.length;
    
    console.log('[MerkleTreeService] Tree serialized', {
      transferId: treeStorage.transferId.slice(0, 8) + '...',
      rawTreeSize,
      serializedSize: storageData.length,
      overhead: storageData.length - rawTreeSize,
      overheadBytes: `${storageData.length - rawTreeSize} bytes (${((storageData.length - rawTreeSize) / rawTreeSize * 100).toFixed(1)}%)`
    });

    // WRITE-BEFORE-READ GUARD: Track this write operation
    // Create a deferred promise that we resolve when write completes
    let resolveWrite: () => void;
    let rejectWrite: (err: any) => void;
    const writePromise = new Promise<void>((resolve, reject) => {
      resolveWrite = resolve;
      rejectWrite = reject;
    });
    this.pendingWrites.set(treeKey, writePromise);
    
    try {
      if (transferPath === 'extension' && this.extensionBridge) {
        // Store via extension for native filesystem
        console.log('[MerkleTreeService] Storing tree via extension', { 
          treeKey, 
          size: storageData.length 
        });
        
        onProgress?.({
          phase: 'storing',
          progress: 0,
          bytesProcessed: 0,
          totalBytes: storageData.length,
          message: 'Storing Merkle tree via extension'
        });
        
        try {
          // Use extension bridge public API to store the serialized tree data
          const result = await this.extensionBridge.invokeExtensionWorker(
            'SAVE_TO_NATIVE',
            {
              fileName: `${treeKey}.merkle`,
              fileData: storageData,
              size: storageData.length,
              transferId: treeStorage.transferId,
              fileType: 'merkle-tree',
              metadata: {
                rootHash: Array.from(treeStorage.rootHash),
                totalChunks: treeStorage.totalChunks,
                chunkSize: treeStorage.chunkSize,
                timestamp: treeStorage.timestamp
              }
            },
            30000 // 30 second timeout for large tree storage
          );
          
          if (!result.success) {
            throw new Error(`Extension tree storage failed: ${result.error}`);
          }
          
          console.log('[MerkleTreeService] Tree stored successfully via extension', {
            treeKey,
            method: result.data?.method,
            downloadId: result.data?.downloadId
          });
          
          onProgress?.({
            phase: 'storing',
            progress: 100,
            bytesProcessed: storageData.length,
            totalBytes: storageData.length,
            message: 'Merkle tree stored via extension'
          });
          
        } catch (error) {
          console.error('[MerkleTreeService] Extension tree storage failed:', error);
          
          // Fallback to OPFS if extension fails
          console.log('[MerkleTreeService] Falling back to OPFS storage');
          await opfsIOWorkerService.write(treeKey, new Uint8Array(storageData));
          
          onProgress?.({
            phase: 'storing',
            progress: 100,
            bytesProcessed: storageData.length,
            totalBytes: storageData.length,
            message: 'Merkle tree stored via OPFS fallback'
          });
        }
      } else {
        // Store via OPFS — routed through I/O worker (sync access handle) when available,
        // falls back to main-thread async OPFS automatically
        console.log('[MerkleTreeService] Storing tree in OPFS', { 
          treeKey, 
          size: storageData.length,
          sizeMB: (storageData.length / (1024 * 1024)).toFixed(2)
        });
        
        try {
          await opfsIOWorkerService.write(treeKey, new Uint8Array(storageData));
          console.log('[MerkleTreeService] Tree saved to OPFS successfully', { 
            treeKey,
            path: treeKey 
          });
        } catch (error) {
          console.error('[MerkleTreeService] Failed to save tree to OPFS', { 
            treeKey, 
            error 
          });
          throw error;
        }
      }

      onProgress?.({
        phase: 'notifying',
        progress: 80,
        bytesProcessed: storageData.length,
        totalBytes: storageData.length,
        message: 'Tree stored, preparing notification'
      });
      
      // WRITE-BEFORE-READ GUARD: Mark write as complete
      resolveWrite!();
    } catch (writeError) {
      // WRITE-BEFORE-READ GUARD: Mark write as failed
      rejectWrite!(writeError);
      throw writeError;
    } finally {
      // Clean up pending write tracker after a short delay
      // (allows any queued reads to see the completed state)
      setTimeout(() => this.pendingWrites.delete(treeKey), 100);
    }
  }

  private async downloadTreeViaFileChannel(
    fileChannel: any,
    availableMessage: MerkleTreeAvailableMessage,
    onProgress?: (progress: TreeTransferProgress) => void
  ): Promise<Uint8Array> {
    
    return new Promise((resolve, reject) => {
      const chunks: Uint8Array[] = [];
      let totalReceived = 0;
      const expectedSize = availableMessage.treeSize;
      let timeoutId: NodeJS.Timeout;

      // Store original handler to restore later
      const originalHandler = fileChannel.onmessage;

      // Set up file channel listener for tree data
      const handleMessage = (event: MessageEvent) => {
        try {
          // Handle both JSON and binary data formats
          let data: any;
          if (typeof event.data === 'string') {
            data = JSON.parse(event.data);
          } else if (event.data instanceof ArrayBuffer || event.data instanceof Uint8Array) {
            // Try to decode binary message if it's Merkle tree related
            // For now, pass through to original handler if not Merkle
            if (originalHandler) originalHandler(event);
            return;
          } else {
            data = event.data;
          }
          
          // Handle error responses
          if (data.type === 'MERKLE_TREE_ERROR' && data.transferId === availableMessage.transferId) {
            clearTimeout(timeoutId);
            fileChannel.onmessage = originalHandler;
            reject(new Error(`Tree download failed: ${data.error}`));
            return;
          }
          
          if (data.type === 'MERKLE_TREE_CHUNK' && data.transferId === availableMessage.transferId) {
            // Handle both Array and Uint8Array formats
            const chunkData = data.chunkData instanceof Uint8Array 
              ? data.chunkData 
              : new Uint8Array(data.chunkData);
            chunks.push(chunkData);
            totalReceived += chunkData.length;

            const progress = Math.min(90, (totalReceived / expectedSize) * 80 + 10);
            onProgress?.({
              phase: 'downloading',
              progress,
              bytesProcessed: totalReceived,
              totalBytes: expectedSize,
              message: `Downloaded ${Math.floor(totalReceived / 1024)}KB of ${Math.floor(expectedSize / 1024)}KB`
            });

            // Check if complete
            if (data.isLast || totalReceived >= expectedSize) {
              clearTimeout(timeoutId);
              fileChannel.onmessage = originalHandler;
              
              // Combine chunks
              const combined = new Uint8Array(totalReceived);
              let offset = 0;
              for (const chunk of chunks) {
                combined.set(chunk, offset);
                offset += chunk.length;
              }
              
              resolve(combined);
            }
          } else if (originalHandler) {
            // Pass through non-Merkle messages to original handler
            originalHandler(event);
          }
        } catch (error) {
          console.error('[MerkleTreeService] Error processing tree chunk', error);
          // Pass to original handler if we can't process
          if (originalHandler) originalHandler(event);
        }
      };

      // Replace the onmessage handler
      fileChannel.onmessage = handleMessage;
      
      // Adaptive timeout based on tree size (min 30s, +10s per GB)
      const fileSizeGB = availableMessage.totalChunks * 4 / 1024; // Assuming 4MB chunks
      const timeoutMs = Math.max(30000, 30000 + (fileSizeGB * 10000));
      
      console.log(`[MerkleTreeService] Setting tree download timeout to ${timeoutMs/1000}s for ${fileSizeGB.toFixed(1)}GB file`);
      
      timeoutId = setTimeout(() => {
        fileChannel.onmessage = originalHandler;
        reject(new Error(`Tree download timeout after ${timeoutMs/1000}s`));
      }, timeoutMs);
    });
  }

  private async parseAndValidateTreeData(
    treeData: Uint8Array, 
    availableMessage: MerkleTreeAvailableMessage
  ): Promise<MerkleTreeStorage> {
    
    // Deserialize tree storage
    const treeStorage = this.deserializeTreeStorage(treeData);
    
    // Validate basic properties
    if (treeStorage.transferId !== availableMessage.transferId) {
      throw new Error('Transfer ID mismatch in tree data');
    }
    
    if (treeStorage.totalChunks !== availableMessage.totalChunks) {
      throw new Error('Chunk count mismatch in tree data');
    }
    
    if (!this.uint8ArrayEquals(treeStorage.rootHash, availableMessage.rootHash)) {
      throw new Error('Root hash mismatch in tree data');
    }
    
    return treeStorage;
  }

  private reconstructTreeFromStorage(treeStorage: MerkleTreeStorage): Uint8Array[][] {
    const tree: Uint8Array[][] = [];
    
    for (let i = 0; i < treeStorage.levels.length; i++) {
      const level: Uint8Array[] = [];
      const levelData = treeStorage.levels[i];
      const levelSize = treeStorage.levelSizes[i];
      
      for (let j = 0; j < levelSize; j++) {
        const hash = levelData.slice(j * 32, (j + 1) * 32);
        level.push(hash);
      }
      
      tree.push(level);
    }
    
    return tree;
  }

  /**
   * Phase 2: Serialize tree storage for chunk 0 padding
   * Public to allow sender to embed tree in chunk 0
   */
  public serializeTreeStorage(treeStorage: MerkleTreeStorage): Uint8Array {
    // Simple binary serialization (could be optimized further)
    const metadata = JSON.stringify({
      transferId: treeStorage.transferId,
      fileHash: treeStorage.fileHash,
      fileName: treeStorage.fileName,
      chunkSize: treeStorage.chunkSize,
      totalChunks: treeStorage.totalChunks,
      levelSizes: treeStorage.levelSizes,
      levelOffsets: treeStorage.levelOffsets,
      timestamp: treeStorage.timestamp,
      transferPath: treeStorage.transferPath,
      treeSize: treeStorage.treeSize,
      generationTime: treeStorage.generationTime,
      memoryUsage: treeStorage.memoryUsage,
      // Phase 1: Transfer recovery metadata (convert Uint8Array to Array for JSON)
      transferMetadata: treeStorage.transferMetadata ? {
        ...treeStorage.transferMetadata,
        lastChunkHash: treeStorage.transferMetadata.lastChunkHash
          ? Array.from(treeStorage.transferMetadata.lastChunkHash)
          : undefined
      } : undefined,
      // Phase 1: Crypto receipt data (convert Uint8Array to Array for JSON)
      receiptData: treeStorage.receiptData ? {
        contentCreator: treeStorage.receiptData.contentCreator,
        transferProof: treeStorage.receiptData.transferProof 
          ? Array.from(treeStorage.receiptData.transferProof) 
          : undefined,
        blockchainRef: treeStorage.receiptData.blockchainRef,
        licenseInfo: treeStorage.receiptData.licenseInfo
      } : undefined
    });
    
    const metadataBytes = new TextEncoder().encode(metadata);
    const rootHash = treeStorage.rootHash;
    
    // Calculate total size
    const levelsSize = treeStorage.levels.reduce((sum, level) => sum + level.length, 0);
    const totalSize = 4 + metadataBytes.length + rootHash.length + levelsSize;
    
    // Serialize
    const result = new Uint8Array(totalSize);
    let offset = 0;
    
    // Metadata length (4 bytes)
    const metadataLengthView = new DataView(result.buffer, offset, 4);
    metadataLengthView.setUint32(0, metadataBytes.length, true);
    offset += 4;
    
    // Metadata
    result.set(metadataBytes, offset);
    offset += metadataBytes.length;
    
    // Root hash
    result.set(rootHash, offset);
    offset += rootHash.length;
    
    // Tree levels
    for (const level of treeStorage.levels) {
      result.set(level, offset);
      offset += level.length;
    }
    
    return result;
  }

  /**
   * Phase 3: Deserialize tree storage from chunk 0 padding
   * Public to allow receiver to extract tree from chunk 0
   */
  public deserializeTreeStorage(data: Uint8Array): MerkleTreeStorage {
    let offset = 0;
    
    // Read metadata length
    const metadataLengthView = new DataView(data.buffer, offset, 4);
    const metadataLength = metadataLengthView.getUint32(0, true);
    offset += 4;
    
    // Read metadata
    const metadataBytes = data.slice(offset, offset + metadataLength);
    const metadataStr = new TextDecoder().decode(metadataBytes);
    const metadata = JSON.parse(metadataStr);
    offset += metadataLength;
    
    // Read root hash
    const rootHash = data.slice(offset, offset + 32);
    offset += 32;
    
    // Read tree levels
    const levels: Uint8Array[] = [];
    for (let i = 0; i < metadata.levelSizes.length; i++) {
      const levelSize = metadata.levelSizes[i] * 32; // 32 bytes per hash
      const level = data.slice(offset, offset + levelSize);
      levels.push(level);
      offset += levelSize;
    }
    
    // Phase 1: Convert transferMetadata.lastChunkHash from Array back to Uint8Array
    const transferMetadata = metadata.transferMetadata ? {
      ...metadata.transferMetadata,
      lastChunkHash: metadata.transferMetadata.lastChunkHash
        ? new Uint8Array(metadata.transferMetadata.lastChunkHash)
        : undefined
    } : undefined;
    
    // Phase 1: Convert receiptData.transferProof from Array back to Uint8Array
    const receiptData = metadata.receiptData ? {
      contentCreator: metadata.receiptData.contentCreator,
      transferProof: metadata.receiptData.transferProof 
        ? new Uint8Array(metadata.receiptData.transferProof) 
        : undefined,
      blockchainRef: metadata.receiptData.blockchainRef,
      licenseInfo: metadata.receiptData.licenseInfo
    } : undefined;
    
    return {
      ...metadata,
      rootHash,
      levels,
      transferMetadata,
      receiptData
    } as MerkleTreeStorage;
  }

  private createAvailableMessage(treeStorage: MerkleTreeStorage): MerkleTreeAvailableMessage {
    return {
      transferId: treeStorage.transferId,
      merkleTreeKey: this.generateTreeKey(treeStorage.transferId),
      treeSize: treeStorage.treeSize,
      rootHash: treeStorage.rootHash,
      chunkSize: treeStorage.chunkSize,
      totalChunks: treeStorage.totalChunks,
      transferPath: treeStorage.transferPath === 'extension' ? 1 : 0,
      timestamp: treeStorage.timestamp
    };
  }

  private async calculateFileHash(file: File): Promise<string> {
    // Use streaming hash for large files to avoid memory allocation
    if (file.size > 100 * 1024 * 1024) { // 100MB threshold
      console.log(`[MerkleTreeService] Using streaming hash for ${(file.size / (1024**3)).toFixed(1)}GB file`);
      
      // Process file in chunks to avoid memory exhaustion
      const CHUNK_SIZE = 64 * 1024 * 1024; // 64MB chunks
      const chunks: Uint8Array[] = [];
      
      // Process file in chunks and accumulate hashes
      for (let offset = 0; offset < file.size; offset += CHUNK_SIZE) {
        const chunk = file.slice(offset, Math.min(offset + CHUNK_SIZE, file.size));
        const chunkBuffer = await chunk.arrayBuffer();
        const chunkHash = await hash(new Uint8Array(chunkBuffer));
        chunks.push(chunkHash);
        
        // Log progress for large files
        if (file.size > 1024 * 1024 * 1024) {
          const progress = Math.round((offset / file.size) * 100);
          if (progress % 10 === 0) {
            console.log(`[MerkleTreeService] File hash progress: ${progress}%`);
          }
        }
      }
      
      // If we have multiple chunks, hash them together
      if (chunks.length > 1) {
        const combinedHashes = new Uint8Array(chunks.length * 32);
        chunks.forEach((chunk, i) => combinedHashes.set(chunk, i * 32));
        const finalHash = await hash(combinedHashes);
        // Convert Uint8Array to hex string without Buffer (browser-compatible)
        return Array.from(finalHash)
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
      } else {
        // Convert Uint8Array to hex string without Buffer
        return Array.from(chunks[0])
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
      }
    }
    
    // For smaller files, use direct approach
    const buffer = await file.arrayBuffer();
    const hashArray = await hash(new Uint8Array(buffer));
    // Convert Uint8Array to hex string without Buffer (browser-compatible)
    return Array.from(hashArray)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private generateTreeKey(transferId: string): string {
    return `merkle_tree_${transferId}`;
  }

  private estimateMemoryUsage(tree: Uint8Array[][]): number {
    return tree.reduce((sum, level) => sum + level.length * 32, 0);
  }

  /**
   * Browser-compatible helper to compare Uint8Arrays
   */
  private uint8ArrayEquals(a: Uint8Array | any, b: Uint8Array | any): boolean {
    // Handle cases where inputs might be raw arrays or other types
    const arrayA = a instanceof Uint8Array ? a : new Uint8Array(a);
    const arrayB = b instanceof Uint8Array ? b : new Uint8Array(b);
    
    if (arrayA.length !== arrayB.length) return false;
    for (let i = 0; i < arrayA.length; i++) {
      if (arrayA[i] !== arrayB[i]) return false;
    }
    return true;
  }

  /**
   * Browser-compatible helper to convert Uint8Array to hex string
   */
  private uint8ArrayToHex(array: Uint8Array | any): string {
    const uint8 = array instanceof Uint8Array ? array : new Uint8Array(array);
    return Array.from(uint8)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // =====================================================================
  // PROGRESS CONSOLIDATION: All progress tracking methods removed.
  // Progress is now handled exclusively by Dexie (provider.tsx dexieProgressCache).
  // The Merkle tree is immutable after creation — written once, read for proofs/recovery.
  // This eliminates the 53KB read-modify-write OPFS storm every 5 seconds during transfer.
  // =====================================================================
}

// Singleton instance
export const merkleTreeService = new MerkleTreeService();

/**
 * Export the service class and singleton for flexible usage
 */
export default MerkleTreeService;
